import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import { gatewayV1Abi } from "@/abi";
import { getGatewayContractAddress } from "@/config";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { type TransactionHash, TOKEN_IDS } from "@/types";
import { prepareFormTokenAmountDecimals } from "@/utils/token";

/**
 * Prepares a create position transaction
 */
export const prepareCreatePositionTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const amountBigInt = prepareFormTokenAmountDecimals(amount, TOKEN_IDS.IUSD);
    const contractAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: contractAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "createPosition",
            args: [amountBigInt, unwindingEpochs, walletClient.account.address],
        }),
    };
};

/**
 * Estimates gas for a create position transaction
 */
export const estimateCreatePositionTransactionGas = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareCreatePositionTransaction(amount, unwindingEpochs, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};

export const gatewayCreatePositionTransaction = async (
    amount: number,
    unwindingEpochs: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    const txRequest = await prepareCreatePositionTransaction(amount, unwindingEpochs, walletClient);
    return writeContractTransaction(walletClient, txRequest);
};
